import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/axiosConfig';
import AuthContext from '../contexts/AuthContext';
import { useOnClickOutside } from '../hooks/useOnClickOutside';
import logo from '../logo.jpg';

const DashboardPage = () => {
    const navigate = useNavigate();
    const { user } = React.useContext(AuthContext);
    // 점검 지표는 전부 회차에서 온다(/slots/summary). 여기서 받는 것은
    // 「우선 조치 사항」 에 세는 미해결 이슈 하나뿐이다.
    const [stats, setStats] = useState({ unresolved: 0 });
    /**
     * 곧 끝나는 계약.
     *
     * 만료 알림은 푸시로 가지만, 그 알림을 못 본 날이 있다. 대시보드는 매일
     * 여는 곳이라 여기 한 줄 있으면 다시 걸린다.
     */
    const [expiring, setExpiring] = useState([]);
    /**
     * 비밀번호를 잊어 손을 든 사람들.
     *
     * 관리자가 링크를 만들어 줘야 풀린다. 사용자 화면에서는 기다리는 것 말고
     * 할 일이 없으므로, 관리자 쪽에서 눈에 띄어야 오래 묵지 않는다.
     */
    const [resetAsks, setResetAsks] = useState([]);
    /**
     * 이번 주에 낀 공휴일.
     *
     * 숫자를 고치지 않는다. 연휴가 낀 주의 낮은 이행률을 '관리가 부실하다' 로
     * 읽는 것만 막는다 -- 건물주도 이 화면을 본다.
     */
    const [holidays, setHolidays] = useState([]);
    const [facilities, setFacilities] = useState([]);
    const [summary, setSummary] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedFacilityId, setSelectedFacilityId] = useState('all');

    const [currentDate] = useState(new Date());

    const dropdownRef = useRef(null);
    useOnClickOutside(dropdownRef, () => setShowFacilityMenu(false));

    const fetchStats = useCallback(async () => {
        setRefreshing(true);
        try {
            const params = selectedFacilityId !== 'all' ? { facilityId: selectedFacilityId } : {};

            const [statsRes, facilitiesRes, summaryRes] = await Promise.all([
                api.get('/reports/stats', { params }),
                api.get('/facilities'),
                api.get('/inspection-plans/slots/summary', {
                    params: selectedFacilityId !== 'all' ? { facilityId: selectedFacilityId } : {},
                }),
            ]);

            setFacilities(facilitiesRes.data);
            setSummary(summaryRes.data);
            setStats({ unresolved: statsRes.data.unresolved || 0 });
        } catch (error) {
            console.error("Failed to fetch stats", error);
        } finally {
            setRefreshing(false);
        }
    }, [selectedFacilityId]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    // 이번 주가 정해진 뒤에 묻는다. 못 받아도 카드는 그대로 그려지므로 흘린다.
    const weekStart = summary?.week?.start;
    const weekEnd = summary?.week?.end;
    useEffect(() => {
        if (!weekStart || !weekEnd) return;
        let alive = true;
        api
            .get('/holidays', { params: { from: weekStart, to: weekEnd } })
            .then((res) => alive && setHolidays(res.data || []))
            .catch(() => alive && setHolidays([]));
        return () => {
            alive = false;
        };
    }, [weekStart, weekEnd]);

    useEffect(() => {
        let alive = true;
        api
            .get('/leases')
            .then((res) => {
                if (!alive) return;
                // 60일 안에 끝나는 진행 중인 계약. 정확한 기준은 계약 목록의
                // '만료 임박' 과 같은 값을 쓴다 -- 두 화면이 다른 날짜를
                // 말하면 어느 쪽을 믿어야 할지 알 수 없다.
                setExpiring(
                    (res.data || []).filter(
                        (l) => l.daysLeft !== null && l.daysLeft <= 60,
                    ),
                );
            })
            // 임대차를 못 보는 사람에게는 그냥 없는 카드다. 403 은 흘린다.
            .catch(() => alive && setExpiring([]));

        api
            .get('/password-resets/requests')
            // 관리자만 볼 수 있다. 아니면 그냥 없는 줄이다.
            .then((res) => alive && setResetAsks(res.data || []))
            .catch(() => alive && setResetAsks([]));

        return () => {
            alive = false;
        };
    }, []);

    const week = summary?.week;
    const trend = summary?.trend || [];
    // 마지막이 이번 주다. 그 앞이 견줄 대상.
    const prevWeek = trend.length > 1 ? trend[trend.length - 2] : null;
    const needsAction = summary?.needsAction || [];
    const todayPending = summary?.today?.pending ?? 0;
    const missed = week?.missed ?? 0;
    const hasPlan = (week?.required ?? 0) > 0;

    // 답을 먼저 말한다. 숫자는 근거로 뒤따른다.
    const verdict = !hasPlan
        ? { tone: 'muted', icon: 'bi-calendar-x', text: '예정된 점검이 없습니다' }
        : missed > 0
            ? { tone: 'danger', icon: 'bi-exclamation-octagon-fill', text: '확인이 필요합니다' }
            : { tone: 'success', icon: 'bi-check-circle-fill', text: '이번 주 순조롭습니다' }; 

    /**
     * 이번 주가 아직 안 끝났는가.
     *
     * 끝난 주와 진행 중인 주는 같은 자로 잰 값이 아니다. 화요일 아침의 19% 는
     * 이틀치를 한 주 몫으로 나눈 값이고, 지난주의 100% 는 일곱 날을 다 마친
     * 값이다. 이 둘을 견줘 빨간 화살표를 그리면, 아무것도 놓치지 않은 주에
     * '나빠졌다' 고 말하게 된다.
     */
    const inProgress = Boolean(
        week?.end && new Date(summary?.date ?? Date.now()) < new Date(week.end),
    );

    const delta = (now, before) => {
        // 진행 중인 주는 견줄 상대가 없다. 대신 무엇으로 이 값이 나왔는지 적는다.
        if (inProgress || before == null) return null;
        const diff = now - before;
        if (diff === 0) return { text: `지난주 ${before}% –`, cls: 'text-muted' };
        return {
            text: `지난주 ${before}% ${diff > 0 ? '↑' : '↓'}`,
            cls: diff > 0 ? 'text-success' : 'text-danger',
        };
    };
    const completionDelta = delta(week?.completionRate ?? 0, prevWeek?.completionRate);
    const onTimeDelta = delta(week?.onTimeRate ?? 0, prevWeek?.onTimeRate);

    const formatDate = (date) => {
        const days = ['일', '월', '화', '수', '목', '금', '토'];
        return `${date.getFullYear()}.${date.getMonth() + 1}.${date.getDate()}(${days[date.getDay()]})`;
    };

    const [showFacilityMenu, setShowFacilityMenu] = useState(false);
    const selectedFacility = facilities.find(f => String(f.id) === String(selectedFacilityId)) || { name: '전체 시설 현황 보기' };

    return (
        <div className="dashboard-page pb-5 bg-light min-vh-100">
            <style>{`
                .hero-section {
                    background: white;
                    border-radius: 20px;
                    padding: 1.5rem 1rem;
                    margin: 0 0 1rem 0;
                    box-shadow: 0 10px 40px rgba(0,0,0,0.04);
                    display: flex;
                    align-items: center;
                    justify-content: space-around;
                    flex-wrap: wrap;
                    border: 1px solid rgba(0,0,0,0.02);
                }
                .chart-container svg {
                    transform: rotate(-90deg);
                }
                .kpi-card:hover {
                    box-shadow: 0 10px 25px rgba(0,0,0,0.05);
                }
                .kpi-card:active {
                    transform: scale(0.96);
                }
                
                .facility-selector-container {
                    position: relative;
                    margin: 1rem 0;
                    padding: 0.1rem;
                    z-index: 100;
                }
                .facility-trigger {
                    background: white;
                    padding: 0.85rem 1.5rem;
                    border-radius: 50px;
                    box-shadow: 0 8px 25px rgba(0,0,0,0.06);
                    display: flex;
                    align-items: center;
                    border: 1px solid #eee;
                    cursor: pointer;
                    transition: all 0.2s;
                    width: 100%;
                }
                .facility-trigger:hover {
                    border-color: var(--primary-color);
                    box-shadow: 0 8px 30px rgba(0,0,0,0.1);
                }
                .facility-menu {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    margin-top: 0.5rem;
                    background: white;
                    border-radius: 20px;
                    box-shadow: 0 15px 50px rgba(0,0,0,0.15);
                    border: 1px solid #eee;
                    overflow: hidden;
                    max-height: 300px;
                    overflow-y: auto;
                    animation: fadeInDown 0.2s ease-out;
                }
                .facility-item {
                    padding: 0.85rem 1.5rem;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    transition: background 0.2s;
                    font-weight: 500;
                    border-bottom: 1px solid #f9fafb;
                }
                .facility-item:last-child { border-bottom: none; }
                .facility-item:hover {
                    background: #f3f0ff;
                    color: var(--primary-color);
                }
                .facility-item.active {
                    background: var(--primary-color);
                    color: white;
                }
                @keyframes spin { to { transform: rotate(360deg); } }
                .spin { display: inline-block; animation: spin 0.7s linear infinite; }

                /* 숫자를 크게 보이려고 rem 을 손으로 박아 두었는데, 손안의
                   화면에서는 두 개가 나란히 서면 줄이 넘친다. */
                .metric-value { font-size: 1.75rem; }
                .verdict-text { font-size: 1.3rem; }
                @media (max-width: 767.98px) {
                    .hero-section { padding: 1.125rem 0.875rem; }
                    .facility-trigger { padding: 0.7rem 1rem; }
                    .facility-item { padding: 0.7rem 1rem; }
                    .metric-value { font-size: 1.4rem; }
                    .verdict-text { font-size: 1.1rem; }
                }
                @keyframes fadeInDown {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>

            <div className="px-2 mb-2 d-flex justify-content-between align-items-center">
                <div>
                    <h4 className="fw-bold mb-1" style={{ color: '#111827' }}>{user?.company?.name || '관리자님'} 👋</h4>
                    <p className="text-muted small mb-0">{formatDate(currentDate)} 실시간 현황</p>
                </div>
                {/* 동작은 하고 있었는데 누른 티가 안 나서 안 되는 것처럼 보였다. */}
                <button
                    className="btn btn-white border shadow-sm rounded-circle btn-icon-circle"
                    onClick={() => fetchStats()}
                    disabled={refreshing}
                    aria-label="새로고침"
                >
                    <i className={`bi bi-arrow-clockwise fs-5 ${refreshing ? 'spin' : ''}`}></i>
                </button>
            </div>

            {/* 모던 커스텀 시설 필터 */}
            <div className="facility-selector-container" ref={dropdownRef}>
                <div className="facility-trigger" onClick={() => setShowFacilityMenu(!showFacilityMenu)}>
                    <i className="bi bi-building-fill text-primary me-2 fs-5"></i>
                    <span className="fw-bold flex-grow-1 text-dark" style={{ fontSize: '1.05rem' }}>
                        {selectedFacility.name}
                    </span>
                    <i className={`bi bi-chevron-${showFacilityMenu ? 'up' : 'down'} text-muted ms-2`}></i>
                </div>

                {showFacilityMenu && (
                    <div className="facility-menu shadow-lg">
                        <div
                            className={`facility-item ${selectedFacilityId === 'all' ? 'active' : ''}`}
                            onClick={() => { setSelectedFacilityId('all'); setShowFacilityMenu(false); }}
                        >
                            <i className="bi bi-grid-fill me-3"></i>
                            전체 시설 현황 보기
                        </div>
                        {facilities.map(f => (
                            <div
                                key={f.id}
                                className={`facility-item ${String(selectedFacilityId) === String(f.id) ? 'active' : ''}`}
                                onClick={() => { setSelectedFacilityId(String(f.id)); setShowFacilityMenu(false); }}
                            >
                                <i className="bi bi-building me-3"></i>
                                {f.name}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* 메인 진행률 차트 */}
            {/* 메인 진행률 차트 동적 렌더링 */}
            {/* 판정을 먼저, 근거는 뒤에. 지난주와 나란히 두지 않으면 92% 가
                좋은지 나쁜지 알 수 없다. */}
            <div className="hero-section flex-column align-items-stretch">
                <div className="d-flex align-items-center gap-2 mb-1">
                    <i className={`bi ${verdict.icon} fs-4 text-${verdict.tone === 'muted' ? 'secondary' : verdict.tone}`}></i>
                    <span className="fw-bold verdict-text">{verdict.text}</span>
                </div>
                <p className="text-muted small mb-3 ps-4 ms-2">
                    {hasPlan
                        ? `미이행 ${missed}건 · 남은 점검 ${week.pending}건`
                        : '이 시설에 예약된 점검 계획이 없습니다.'}
                </p>

                {hasPlan && (
                    <div className="ps-4 ms-2">
                        {/* 두 지표를 두 칸으로 나눠 둔다. 예전에는 가로로 늘어놓고
                            사이에 세로 구분선을 뒀는데, 폭이 좁아 줄바꿈되면 그
                            선이 왼쪽에 남아 들여쓰기처럼 보였다. */}
                        <div className="row g-2">
                            <div className="col-6">
                                <div className="text-muted small mb-1">이행률</div>
                                <div className="fw-bold lh-1 metric-value">{week.completionRate}%</div>
                                {completionDelta ? (
                                    <div className={`small mt-1 ${completionDelta.cls}`}>{completionDelta.text}</div>
                                ) : (
                                    /* 지난주와 견주지 못하는 대신 분자·분모를 그대로
                                       보여준다. 19% 가 왜 19% 인지가 여기서 읽힌다. */
                                    <div className="small mt-1 text-muted">
                                        {week.required}회 중 {week.completed}회
                                    </div>
                                )}
                            </div>
                            <div className="col-6">
                                <div className="text-muted small mb-1">정시율</div>
                                <div className="fw-bold lh-1 metric-value">{week.onTimeRate}%</div>
                                {onTimeDelta ? (
                                    <div className={`small mt-1 ${onTimeDelta.cls}`}>{onTimeDelta.text}</div>
                                ) : (
                                    <div className="small mt-1 text-muted">정시 {week.onTime}회</div>
                                )}
                            </div>
                        </div>

                        {holidays.length > 0 && (
                            <div className="mt-2" style={{ fontSize: '0.78rem', color: '#dc2626' }}>
                                <i className="bi bi-calendar-event me-1"></i>
                                이번 주 공휴일 {holidays.length}일
                                <span className="text-muted ms-1">
                                    ({holidays.map((h) => h.name).join(', ')})
                                </span>
                            </div>
                        )}

                        {trend.length > 1 && (
                            <div className="mt-3 pt-3 border-top">
                                {/* 막대만 있으면 무엇인지 알 수 없다. */}
                                {/* 마지막 칸이 진행 중이라는 말은 적지 않는다.
                                    점선 테두리와 옅은 색이 이미 그 말을 하고
                                    있고, 글로 한 번 더 하면 그림을 못 믿는다는
                                    뜻이 된다. */}
                                <div className="text-muted small mb-1">
                                    최근 {trend.length}주 이행률
                                </div>
                                {/* 막대만 그려 두면 어디까지가 가득 찬 것인지 알 수
                                    없다. 100% 자리를 옅은 바탕으로 깔면 눈금도 설명도
                                    없이 얼마나 찼는지 읽힌다. 다만 정확한 값은 숫자로
                                    적는다 -- 25% 와 30% 는 40px 안에서 구분되지 않는다. */}
                                <div className="d-flex gap-1">
                                    {trend.map((w, i) => {
                                        const last = i === trend.length - 1;
                                        // 마지막 칸은 아직 채워지는 중이다. 다 채운
                                        // 주들과 같은 색으로 그리면 '떨어졌다' 로
                                        // 읽힌다.
                                        const running = last && inProgress;
                                        return (
                                            /* 폭을 늘어나는 대로 두면 넓은 화면에서 카드
                                               절반을 막대가 차지해 옆 숫자들과 무게가
                                               맞지 않는다. 여섯 주치는 좁아도 흐름이
                                               읽히므로 칸을 28px 로 묶고 왼쪽에 붙인다. */
                                            <div key={i} className="text-center" style={{ flex: 1, maxWidth: '28px' }}>
                                                <div
                                                    style={{
                                                        height: '40px',
                                                        background: '#f3f4f6',
                                                        borderRadius: '4px',
                                                        display: 'flex',
                                                        alignItems: 'flex-end',
                                                        overflow: 'hidden',
                                                        // 진행 중인 칸은 테두리만 둘러
                                                        // '아직 열려 있다' 를 표시한다.
                                                        ...(running
                                                            ? { border: '1px dashed #a5b4fc', background: 'transparent' }
                                                            : {}),
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: '100%',
                                                            height: `${Math.max(2, w.completionRate)}%`,
                                                            background: running
                                                                ? '#e0e7ff'
                                                                : last
                                                                    ? 'var(--primary-color)'
                                                                    : '#c7d2fe',
                                                        }}
                                                    />
                                                </div>
                                                <div
                                                    className={last && !running ? 'fw-bold' : 'text-muted'}
                                                    style={{ fontSize: '0.62rem', lineHeight: 1.6 }}
                                                >
                                                    {w.completionRate}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* 우선 조치 사항. 없으면 아예 나오지 않는다. */}
            {(needsAction.length > 0 || stats.unresolved > 0 || expiring.length > 0 || resetAsks.length > 0) && (
                <div className="bg-white rounded-4 border p-3 mb-4">
                    <div className="text-muted small fw-bold mb-2">우선 조치 사항</div>

                    {needsAction.slice(0, 4).map((slot) => (
                        <div
                            key={slot.id}
                            className="d-flex align-items-center gap-2 py-2 border-bottom"
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigate('/inspection-matrix')}
                        >
                            <i className={`bi ${slot.status === 'MISSED' ? 'bi-x-circle-fill text-danger' : 'bi-clock-fill text-warning'}`}></i>
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <div className="small text-truncate">
                                    {slot.planName || slot.plan?.name} · {slot.labelText}
                                </div>
                                {/* 어디가 남았는지까지 말해 준다. '한 회차 밀렸다' 만으로는
                                    어디로 가야 할지 알 수 없다. */}
                                {slot.remaining?.length > 0 && (
                                    <div className="text-muted text-truncate" style={{ fontSize: '0.72rem' }}>
                                        {slot.remaining
                                            .slice(0, 2)
                                            .map((r) => r.section?.name || `${r.facility?.name} 전체`)
                                            .join(', ')}
                                        {slot.remaining.length > 2 && ` 외 ${slot.remaining.length - 2}곳`}
                                    </div>
                                )}
                            </div>
                            <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                {slot.status === 'MISSED' ? '미이행' : '마감 임박'}
                            </span>
                        </div>
                    ))}

                    {/* 사람이 기다리고 있는 일이라 이슈보다 위에 둔다. 그 사람은
                        지금 로그인을 못 하고 있고, 우리가 링크를 만들기 전까지
                        할 수 있는 일이 없다. */}
                    {resetAsks.length > 0 && (
                        <Link
                            to="/workers"
                            className="d-flex align-items-center gap-2 py-2 border-bottom text-decoration-none text-dark"
                        >
                            <i className="bi bi-key-fill text-danger"></i>
                            <span className="small flex-grow-1 text-truncate">
                                {resetAsks[0].user.name}님 비밀번호 재설정 요청
                                {resetAsks.length > 1 && ` 외 ${resetAsks.length - 1}건`}
                            </span>
                            <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                링크 발급
                            </span>
                        </Link>
                    )}

                    {stats.unresolved > 0 && (
                        <Link
                            to="/reports"
                            state={{ facilityId: selectedFacilityId, resolved: false }}
                            className="d-flex align-items-center gap-2 py-2 text-decoration-none text-dark"
                        >
                            <i className="bi bi-tools text-warning"></i>
                            <span className="small flex-grow-1">미해결 이슈</span>
                            <span className="text-muted" style={{ fontSize: '0.75rem' }}>{stats.unresolved}건</span>
                        </Link>
                    )}

                    {/* 가장 급한 것 하나만 이름으로 부른다. 여럿이면 몇 건인지
                        덧붙인다 -- 계약 넷을 여기 늘어놓으면 오늘 할 점검이
                        묻힌다. */}
                    {expiring.length > 0 && (
                        <Link
                            to="/leases"
                            className="d-flex align-items-center gap-2 py-2 text-decoration-none text-dark"
                        >
                            <i className="bi bi-hourglass-split text-warning"></i>
                            <span className="small flex-grow-1 text-truncate">
                                {expiring[0].tenant?.name} 계약 만료
                                {expiring.length > 1 && ` 외 ${expiring.length - 1}건`}
                            </span>
                            <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                {expiring[0].daysLeft === 0
                                    ? '오늘'
                                    : `${expiring[0].daysLeft}일 남음`}
                            </span>
                        </Link>
                    )}
                </div>
            )}

            {/* 오늘 남은 것은 비율이 아니라 개수로 말한다. 오전 9시에 25% 는
                정상인데도 나쁜 신호처럼 읽힌다. */}
            <div
                className="bg-light rounded-3 px-3 py-2 d-flex align-items-center justify-content-between mb-4"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/inspection-matrix')}
            >
                <span className="small text-muted">오늘 남은 점검 {todayPending}건</span>
                <span className="small text-primary fw-bold">
                    주간 점검 현황 <i className="bi bi-arrow-right"></i>
                </span>
            </div>

            <div className="text-center mt-5 mb-3 opacity-50">
                <img src={logo} alt="logo" height="32" className="grayscale mb-2 rounded" />
                <div className="text-muted" style={{ fontSize: '0.65rem' }}>© 2026 Sunheung Company, Inc. All rights reserved.</div>
            </div>
        </div>
    );
};

export default DashboardPage;
